/** Kapitel „Schwachstellen“ im Ergebnisbericht, analog zum Backend. */
export const VULN_CHAPTER = 3;

export const SCHWACHSTELLE_REF_ATTR = 'data-schwachstelle-ref';

export interface FindingReferenceOption {
  id: number;
  titel: string;
  /** Aktuelle Kapitelnummer, z. B. `3.2`. */
  kapitel: string;
  /** Schwachstellenklasse in der Report-Sprache. */
  klasse?: string;
}

export function findingChapterLabel(position1Based: number): string {
  return `${VULN_CHAPTER}.${position1Based}`;
}

/**
 * Kapitelnummern aus der Listenreihenfolge (dieselbe Sortierung wie der Report).
 * ``sort_order`` ist keine Kapitelnummer und wird hier nicht gelesen.
 */
export function findingKlasseName(
  klasse: { name_de?: string | null; name_en?: string | null } | null | undefined,
  lang: 'de' | 'en' = 'de',
): string {
  if (!klasse) return '';
  if (lang === 'en' && klasse.name_en) return klasse.name_en;
  return klasse.name_de || '';
}

export function findingRefTargets(
  rows: Array<{ id: number; titel: string; klasse?: string }>,
): FindingReferenceOption[] {
  return rows.map((row, index) => ({
    id: row.id,
    titel: row.titel,
    klasse: row.klasse || '',
    kapitel: findingChapterLabel(index + 1),
  }));
}

/** `3.1 – Klasse – Titel`, ohne das Wort „Kapitel“. */
export function formatFindingRef(
  target: { kapitel: string; titel: string; klasse?: string },
  lang: 'de' | 'en' = 'de',
): string {
  const titel = target.titel.trim() || (lang === 'en' ? 'Untitled' : 'Ohne Titel');
  const klasse = (target.klasse || '').trim();
  if (klasse) return `${target.kapitel} – ${klasse} – ${titel}`;
  return `${target.kapitel} – ${titel}`;
}

export function missingFindingRefLabel(lang: 'de' | 'en', id: number): string {
  if (lang === 'en') return `Finding #${id} (not in this audit)`;
  return `Schwachstelle #${id} (nicht im Audit)`;
}

/** Satzzeichen, vor denen kein zusätzliches Leerzeichen steht. */
const STICKY_AFTER_REF = '.,;:!?)]}';
/** Zeichen, nach denen der Verweis ohne Extra-Leerzeichen beginnt. */
const STICKY_BEFORE_REF = '([{"„«\'';

/**
 * Nach dem Referenz-Knoten ein Leerzeichen, wenn direkt ein Wort folgt.
 * Vor Leerzeichen oder Satzzeichen keines, sonst entsteht „Konfiguration .“.
 */
export function needsSpaceAfterFindingRef(following: string): boolean {
  const ch = following[0];
  if (!ch || /\s/.test(ch) || STICKY_AFTER_REF.includes(ch)) return false;
  return true;
}

/** Vor dem Referenz-Knoten ein Leerzeichen, wenn direkt ein Wort vorausgeht. */
export function needsSpaceBeforeFindingRef(preceding: string): boolean {
  const ch = preceding[preceding.length - 1];
  if (!ch || /\s/.test(ch) || STICKY_BEFORE_REF.includes(ch)) return false;
  return true;
}
